import { useMemo } from 'react'

import { Link } from 'react-router-dom'

import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useBillingProducts,
  useModifySubscriptionAddon,
  useNetwork,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { LoadingOverlay } from '@tribeplatform/react-ui-kit/LoadingOverlay'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useCurrentProductInformation } from '../../Billing/hooks/useCurrentProductInformation.js'
import { EMAIL_DIGEST_ADDON_CATEGORY } from '../../Store/Addon/constants.js'
import { getAddonPrice } from '../../Store/utils/getAddonPrice.js'
import { TabHeader } from '../../TabView/index.js'

export const EmailDigestAddonCard = () => {
  const { $t } = useI18n()
  const { data: billingProducts, isLoading: isBillingProductsLoading } =
    useBillingProducts()
  const { getAddonInformation, currentInterval, currentProductOrder } =
    useCurrentProductInformation()
  const { mutateAsync: modifySubscriptionAddon, isLoading: installLoading } =
    useModifySubscriptionAddon()
  const addon = useMemo(
    () =>
      billingProducts?.addons.find(product =>
        product?.categories.includes(EMAIL_DIGEST_ADDON_CATEGORY),
      ),
    [billingProducts?.addons],
  )
  const addonInformation = useMemo(
    () => getAddonInformation(addon?.id),
    [addon?.id, getAddonInformation],
  )
  const price = useMemo(
    () => getAddonPrice({ addon, addonInformation, currentInterval }),
    [addon, addonInformation, currentInterval],
  )
  const { data: network } = useNetwork({
    fields: {
      settings: 'all',
    },
  })

  const requiredPlanOrder = addon?.requiredPlanOrder ?? 0
  const hasRequiredPlan = currentProductOrder >= requiredPlanOrder

  const networkCanSendEmailNotifications = !!network?.settings?.emailEnabled
  const addonImageUrl = addon?.imageUrls?.at(0)
  const isInstalled = addonInformation?.quantity > 0

  const buyAddon = async (quantity = 1) => {
    try {
      await modifySubscriptionAddon({
        priceId: price?.id,
        quantity,
      })
      toast({
        title: $t({
          defaultMessage: 'Add-on updated',
          id: 'Admin.Billing.AddonUpdated',
        }),
        description: $t({
          defaultMessage:
            'Your add-on is updated. Changes may take a few moments to appear in your account.',
          id: 'Admin.Billing.AddonUpdated.SuccessDescription',
        }),
        status: 'success',
      })
    } catch (e) {
      toast({
        title: $t({
          defaultMessage: 'Something went wrong',
          id: 'Admin.Billing.SomethingWentWrong',
        }),
        status: 'error',
      })
    }
  }

  return (
    <>
      <TabHeader
        title={
          <div className="flex items-center gap-x-1">
            <T
              defaultMessage="{MEMBER_CC} email digest"
              id="Settings.Notification.EmailDigest"
            />
            <Badge variant="positive">
              <T defaultMessage="New" id="Generics.New" />
            </Badge>
          </div>
        }
        subtitle={
          <T
            defaultMessage="Allow {MEMBERS} to receive daily or weekly email summaries tailored to their space subscriptions and notifications."
            id="Admin.EmailDigestSettings.Description"
          />
        }
      />
      <div className="w-full flex px-8 py-5">
        <div className="max-w-3xl w-full">
          {!networkCanSendEmailNotifications ? (
            <Alert status="info">
              <T
                defaultMessage="Email Digests aren’t being sent because you’ve turned off all emails."
                id="Admin.EmailDigestSettings.NotificationsDisabledDescription"
              />
            </Alert>
          ) : (
            <LoadingOverlay isLoading={isBillingProductsLoading || !addon}>
              <Card className="bg-surface-subdued">
                <Card.Content>
                  <div className="flex items-center gap-x-3 justify-between">
                    <Avatar src={addonImageUrl} size="4x" />
                    <div className="flex flex-col gap-y-2 flex-grow">
                      <h3 className="text-heading-xs font-medium text-content">
                        {addon?.name}
                      </h3>
                      <p className="text-content-subdued text-sm">
                        {addon?.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-x-2">
                      {(!isInstalled || installLoading) && (
                        <>
                          {hasRequiredPlan ? (
                            <Button
                              variant="primary"
                              onClick={() => buyAddon()}
                              loading={
                                installLoading || isBillingProductsLoading
                              }
                              disabled={
                                installLoading || isBillingProductsLoading
                              }
                            >
                              <T
                                defaultMessage="Install"
                                id="Generics.Install"
                              />
                            </Button>
                          ) : (
                            <Button
                              variant="primary"
                              as={Link}
                              to={adminRoutes.appStore.addons.detail(addon?.id)}
                              loading={
                                installLoading || isBillingProductsLoading
                              }
                              disabled={
                                installLoading || isBillingProductsLoading
                              }
                            >
                              <T
                                defaultMessage="Install"
                                id="Generics.Install"
                              />
                            </Button>
                          )}
                        </>
                      )}
                      {isInstalled && !installLoading && (
                        <Button
                          variant="primary"
                          as={Link}
                          to={adminRoutes.appStore.addons.detail(addon?.id)}
                          disabled={installLoading}
                        >
                          <T defaultMessage="Settings" id="Generics.Settings" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card.Content>
              </Card>
            </LoadingOverlay>
          )}
        </div>
      </div>
    </>
  )
}
